<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useAuthStore } from "~/plugins/store/auth";
import { translation, useTranslationStore } from "~/plugins/store/translations";
import {
  createDefaultUserDataValidation,
  type UserDataValidation
} from "~/types/Customer";
import type { TranslationList } from "~/types/Utils";
import { useNuxtApp } from "#app";
import { useUserStore } from "~/plugins/store/user";
import { useReCaptcha } from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import type { ApiVehicleDetails } from "~/types/Vehicle";
import { useVehicleStore } from "~/plugins/store/vehicle";

export default defineComponent({
  components: { AccountLayout, LoadingDialog },
  setup() {
    definePageMeta({
      middleware: ['auth'],
    });

    useHead({
      title: 'Xiga | Masinile mele',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const validationUserDataStatus = ref<UserDataValidation>(createDefaultUserDataValidation());
    const { $toast } = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(true);
    const messageLoadingDialog = ref('Se incarcă...');
    const vehicles = ref([] as ApiVehicleDetails[]);
    const vehicleStore = useVehicleStore();

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (accessToken) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = 'Se încarcă...';
        vehicles.value = await vehicleStore.getVehicles(accessToken);
        if (vehicles.value.length) {
          vehicles.value = vehicles.value.filter(vehicle => vehicle.id);
        }
        showLoadingDialog.value = false;
      }
    });

    const handleValidationUserStatus = ({ field, isValid }: { field: string, isValid: boolean }) => {
      validationUserDataStatus.value[field] = isValid;
    };

    const handleValidation = (field: string, status: boolean) => {
      if (!status) {
        console.log(`Validation failed for field: ${field}`);
        window.dispatchEvent(new CustomEvent('validate-customer', { detail: field }));
        return false;
      }
      return true;
    };

    const getManufacturerLogo = (manufacturer: string) => {
      const imagePath = '/images/cars/' + manufacturer + '.png';
      return imageExists(imagePath) ? imagePath : '/images/cars/no-make.png';
    };

    const imageExists = (url: string) => {
      try {
        const http = new XMLHttpRequest();
        http.open('HEAD', url, false);
        http.send();
        return http.status !== 404;
      } catch (error) {
        return false;
      }
    };

    const getExpirationClass = (expirationDate: string | null | undefined) => {
      if (!expirationDate) return 'text-red';
      const now = new Date();
      const expiration = new Date(expirationDate);
      const diffTime = expiration.getTime() - now.getTime();
      const diffDays = diffTime / (1000 * 3600 * 24);

      if (diffDays < 0) {
        return 'text-red';
      } else if (diffDays < 60) {
        return 'text-warning';
      } else {
        return 'text-success';
      }
    };

    const formatRegistrationPlate = (plate: string): string => {
      const regex = /^([A-Z]{1,2})(\d{2,3})([A-Z]{3})$/;
      const match = plate.match(regex);

      if (match) {
        const [, prefix, numbers, suffix] = match;
        return `${prefix} ${numbers} ${suffix}`;
      }

      return plate; // Returnează placa originală dacă nu se potrivește formatul
    };


    return {
      user,
      accessToken,
      translationList,
      translation,
      handleValidationUserStatus,
      showLoadingDialog,
      messageLoadingDialog,
      vehicles,
      getManufacturerLogo,
      getExpirationClass,
      formatRegistrationPlate
    };
  }
});
</script>

<template>
  <account-layout class="profile ">
    <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
      <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
      <v-row>
        <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="fw-700">
            {{ translation(translationList.my_profile, 'Mașinile mele') }}</h3>
          <p class="fw-400">
            {{
              translation(translationList.my_profile_helper, 'Aici puteți vizualiza și modifica datele mașinilor dumneavoastră')
            }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="vehicles && vehicles.length">
        <v-col v-for="(vehicle, index) in vehicles" :key="index" cols="12" sm="12" md="6">
<!--          TODO WIP -->
<!--           @click="navigateTo('/account/vehicles/' + vehicle.id + '/')" -->
          <v-card rounded="xl" color="gray-light" variant="flat" link>
            <v-row class="ma-2">
              <v-col cols="12" sm="5" md="12" lg="4" class="d-flex align-center justify-center">
                <div style="width: 100px; height: 100px;" class="d-flex align-center justify-center">
                  <NuxtImg
                      :src="getManufacturerLogo(vehicle.rca_vehicle_make_name)"
                      class="car-logo-image"
                      fit="cover"
                      alt="Homepage family"
                      loading="lazy"
                  />
                </div>
              </v-col>
              <v-col cols="11" sm="6" md="10" lg="7">
                <v-row>
                  <v-col cols="12" sm="12" md="12">
                    <div v-if="vehicle.registration_plate" class="fs-18 fw-700">
                      {{ formatRegistrationPlate(vehicle.registration_plate) }}
                    </div>
                    <div v-else class="fw-700">
                      {{ vehicle.rca_vehicle_make_name }} {{ vehicle.rca_vehicle_model_name }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12" md="12">
                    <div v-if="vehicle.rca_vehicle_make_name" class="fw-500">
                      {{ vehicle.rca_vehicle_make_name }} {{ vehicle.rca_vehicle_model_name }}
                    </div>
                    <div v-else-if="vehicle.registration_plate">
                      {{ formatRegistrationPlate(vehicle.registration_plate) }}
                    </div>
                    <div v-if="vehicle.vin">
                      {{ vehicle.vin }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12" md="12">
                    <div class="fw-500">
                      {{ translation(translationList.manufacture_year, 'Dată expirare RCA') }}:
                    </div>
                    <div :class="getExpirationClass(vehicle.default_vehicle_expiration?.rca_expires_at)">
                      {{ vehicle.default_vehicle_expiration?.rca_expires_at || 'Nu există date' }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="1" sm="1" md="2" lg="1" class="d-flex justify-end align-center">
                <v-icon class="" size="x-large">mdi-chevron-right</v-icon>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-skeleton-loader v-else type="image" class="skeleton-h-250"></v-skeleton-loader>
    </v-card>
  </account-layout>
</template>

<style scoped>

</style>
